<template>
  <LeadHistoryCard 
    v-bind="attributes"
    :pick-up-call-target-id="pickUpCallTargetId"
    @update:pick-up-call-target-id="$emit('update:pickUpCallTargetId', $event)"
  >
    <template #date>
      <div>{{ historiedAt }}</div>
    </template>
    <BError 
      v-if="isFail"
      :error-title="failedTitle"
      :error-info="failedReason"
    />
  </LeadHistoryCard>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { CallTarget } from '@/api/openapi';
import LeadHistoryCard from '@/components/organisms/user/lead/history/LeadHistoryCard.vue';
import { SequenceStepInstanceHistory } from '@/store/types';
import { formatShorterDateTime } from '@/utils/date-time';

type TProps = {
  callTargetId: number;
  sequenceStepInstanceHistory: SequenceStepInstanceHistory;
  callTarget?: CallTarget;
  expand?: boolean;
  editable?: boolean;
};

const props = withDefaults(defineProps<TProps>(), {
  callTarget: undefined,
  editable: true,
});
defineEmits<{
  'update:pickUpCallTargetId': [value: number];
}>();
defineOptions({
  inheritAttrs: false,
});

// v-model for pickUpCallTargetId to pass through to parent
const pickUpCallTargetId = defineModel<number>('pickUpCallTargetId');
const i18n = useI18n();

const attributes = computed(() => ({
  text: title(),
  isDisplaySystemUser: true,
  iconComponent: 'BCustomIcon',
  customIconClass: 'b-sequence',
  iconType: 'hot',
  toggle: isFail.value,
  expand: isFail.value ? props.expand : false,
  callTargetId: props.callTargetId,
  history: props.sequenceStepInstanceHistory,
  callTarget: props.callTarget,
}));
const historiedAt = computed<string>(() => formatShorterDateTime(props.sequenceStepInstanceHistory.historiedAt));
const title = () => {
  return `${props.sequenceStepInstanceHistory.actionTypeText} - ${props.sequenceStepInstanceHistory?.name} - ${props.sequenceStepInstanceHistory?.sortOrder + 1}`;
};
const isFail = computed<boolean>(() => props.sequenceStepInstanceHistory.status === 'fail');

const failedTitle = computed(() => {
  if (!isFail.value) return '';
  if (props.sequenceStepInstanceHistory.failedReason === 'systemError') {
    return i18n.t('sequence.step.failedTitle.error');
  } else {
    return i18n.t('sequence.step.failedTitle.sentError');
  }
});
const failedReason = computed(() => {
  if (!isFail.value) return '';
  return i18n.t(`sequence.step.failedReason.${props.sequenceStepInstanceHistory.failedReason}`);
});
</script>

<style lang="scss" scoped>
:deep(.lead-history-expand-indicator) {
  height: 0;
}
</style>
